import React from 'react';
import {
  addDays,
  startOfDay,
  endOfDay,
  subMilliseconds,
  subYears,
} from '@qover/utils/src/date';
import {
  arrayChildValidator,
  createValidator,
  dateDifferenceInDaysTrigger,
  dependsOn,
  dependsOnDiff,
  email,
  isTrue,
  maxDate,
  maxDateAdult,
  maxAbsoluteDateDifferenceInDays,
  minDate,
  minDateAdult,
  minDateDependsOn,
  pattern,
  required,
} from '@qover/utils/src/validation';
import { TripType } from '@qover-private/protos-web/qover/dojo/travel/v1/terms';
import { EntityType } from '@qover-private/protos-web/qover/dojo/contracts/v1/policyholder';

const adultValidator = createValidator({
  birthdate: [
    required,
    minDateAdult(addDays(subYears(new Date(), 76), 1)),
    maxDateAdult(subYears(new Date(), 18)),
  ],
  firstName: [required],
  lastName: [required],
  title: [required],
});

const childValidator = createValidator({
  birthdate: [required, maxDate(new Date())],
  firstName: [required],
  lastName: [required],
});

export const insurersValidation = createValidator({
  'risk.adultsList': arrayChildValidator((values: Array<any>) =>
    values.map(adultValidator),
  ),
  'risk.childrenList': arrayChildValidator((values: Array<any>) =>
    values.map(childValidator),
  ),
});

export const tripDurationValidator = createValidator({
  startDate: [
    required,
    minDate(startOfDay(addDays(new Date(), 1))),
    maxDate(endOfDay(new Date('July 31, 2021'))),
    dateDifferenceInDaysTrigger(
      'endDate',
      60,
      maxDate(endOfDay(addDays(new Date(), 90))),
    ),
  ],
  endDate: [
    required,
    minDateDependsOn('startDate'),
    maxAbsoluteDateDifferenceInDays('startDate', 59, <div />),
  ],
});

export const policyholderValidation = createValidator({
  'policyholder.address.street': [required],
  'policyholder.address.number': [required],
  'policyholder.address.zip': [required, pattern(/[0-9]{4}/)],
  'policyholder.address.city': [required],
  'policyholder.phone': [required],
  'policyholder.email': [required, email],
  'policyholder.title': [required],
  'policyholder.firstName': [required],
  'policyholder.lastName': [required],
  'policyholder.birthdate': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_PERSON,
      required,
    ),
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_PERSON,
      maxDate(subYears(new Date(), 18)),
    ),
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_PERSON,
      minDate(addDays(subYears(new Date(), 76), 1)),
    ),
  ],
  'policyholder.vatIn': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      required,
    ),
  ],
  'policyholder.companyName': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      required,
    ),
  ],
  'policyholder.billingAddress.street': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      dependsOn('policyholder.billingAddressSameAsCompany', false, required),
    ),
  ],
  'policyholder.billingAddress.number': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      dependsOn('policyholder.billingAddressSameAsCompany', false, required),
    ),
  ],
  'policyholder.billingAddress.zip': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      dependsOn('policyholder.billingAddressSameAsCompany', false, required),
    ),
  ],
  'policyholder.billingAddress.city': [
    dependsOn(
      'policyholder.entityType',
      EntityType.ENTITY_TYPE_COMPANY,
      dependsOn('policyholder.billingAddressSameAsCompany', false, required),
    ),
  ],
  startDate: [
    required,
    minDate(startOfDay(addDays(new Date(), 1))),
    dependsOnDiff(
      'terms.tripType',
      TripType.TRIP_TYPE_SINGLE_TRIP,
      maxDate(subMilliseconds(endOfDay(new Date('July 31, 2021')), 1)),
    ),
  ],
});

export const summaryValidation = createValidator({
  clientAgreement: [required, isTrue],
});
